import { useEffect, useState } from 'react';

import { TagEntity } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { GearbeltAssetType } from '@site-mate/sitemate-global-shared/lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { assetCategoryKeys, getAssetCategoriesQueryOptions } from '@/queries/asset-categories';
import { assetKeys, getManufacturersQueryOptions, getTagsQueryOptions, qrCodeKeys } from '@/queries/assets';
import { ApiService } from '@/services/api-v2';

import { Alert, AlertDescription } from '../ui/alert';
import { Form } from '../ui/form';
import { useToast } from '../ui/toaster';
import {
  AssetCreatableMultiSelectField,
  AssetCreatableSelectField,
  assetFormSchema,
  AssetInputField,
  AssetSelectField,
  AssetTextareaField,
} from './asset-form';
import BulkAssetImportDialog from './bulk-asset-import-dialog';

export default function AssetDrawer({
  open: openProp,
  onOpenChange,
  noTrigger = false,
  onOpenBulkImport,
  callback,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean, success?: boolean) => void;
  noTrigger?: boolean;
  onOpenBulkImport?: (open: boolean) => void;
  callback?: (data: z.infer<typeof assetFormSchema>) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(openProp ?? false);
  const [openBulkImport, setOpenBulkImport] = useState(false);

  useEffect(() => {
    setOpen(openProp ?? false);
  }, [openProp]);

  const form = useForm<z.infer<typeof assetFormSchema>>({
    resolver: zodResolver(assetFormSchema),
    defaultValues: {
      categoryId: '',
      class: '',
      comments: '',
      engineNumber: '',
      internalId: '',
      manufacturer: '',
      model: '',
      registration: '',
      serialNumber: '',
      tags: [],
      type: undefined,
      vin: '',
      year: undefined,
    },
  });

  const { data: manufacturers } = useQuery(getManufacturersQueryOptions());
  const { data: assetCategories } = useQuery(getAssetCategoriesQueryOptions());
  const { data: tags } = useQuery(getTagsQueryOptions({ entity: TagEntity.Asset }));

  const { mutateAsync: createAsset } = useMutation({
    mutationFn: (values: z.infer<typeof assetFormSchema>) => {
      return ApiService.CreateAsset({
        internalId: values.internalId,
        type: values.type,
        chassisVin: values.vin,
        class: values.class,
        engineNumber: values.engineNumber,
        manufacturer: values.manufacturer,
        model: values.model,
        registration: values.registration,
        serialNumber: values.serialNumber,
        year: values.year ? Number(values.year) : undefined,
        categoryId: values.categoryId || undefined,
        tags: values.tags?.map(({ value }) => value) || [],
        comments: values.comments,
      });
    },
    onSuccess: async () => {
      await queryClient.resetQueries({
        queryKey: assetKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: qrCodeKeys.all,
      });
      handleOpenChange(false, true);
      callback?.(form.getValues());
      toast.success('Asset added successfully');
    },
    onError: (error) => {
      if (error instanceof HTTPError) {
        if (error.response.status === 409) {
          // Parse the JSON response body
          error.response
            .json()
            .then((errorData) => {
              const errorMessage = errorData.message || error.response.statusText;
              form.setError('internalId', {
                type: 'manual',
                message: errorMessage,
              });
            })
            .catch(() => {
              toast.error(`${error.response.statusText}`);
            });
          return;
        }
      }

      toast.error(`Error adding asset: ${error.message}`);
    },
  });

  const { mutateAsync: createAssetCategory } = useMutation({
    mutationFn: async (values: z.infer<typeof assetFormSchema>) => {
      if (!values.categoryId) throw new Error('Category is required');
      return await (
        await ApiService.CreateWorkspaceAssetCategory({
          name: values.categoryId,
          assetType: values.type,
        })
      ).json();
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: assetCategoryKeys.all,
      });
    },
    onError: (error) => {
      toast.error(`Error adding asset category: ${error.message}`);
    },
  });

  const handleOpenChange = (open: boolean, success?: boolean) => {
    onOpenChange?.(open, success);
    setOpen(open);

    if (!open) form.reset();
  };

  const handleSubmit = async (data: z.infer<typeof assetFormSchema>) => {
    const categoryIdIsValidUUID = z.string().uuid().safeParse(data.categoryId).success;

    if (data.categoryId && !categoryIdIsValidUUID) {
      const category = await createAssetCategory(data);
      await createAsset({ ...data, categoryId: category.id });
    } else {
      await createAsset(data);
    }
  };

  return (
    <>
      {!onOpenBulkImport && <BulkAssetImportDialog open={openBulkImport} onOpenChange={setOpenBulkImport} />}
      <Sheet open={open} onOpenChange={handleOpenChange}>
        {!noTrigger && (
          <SheetTrigger asChild>
            <Button className="h-full rounded-none p-2" variant="success">
              Create Asset
            </Button>
          </SheetTrigger>
        )}
        <SheetContent>
          <Form {...form}>
            <form className="flex h-full flex-col" onSubmit={form.handleSubmit(handleSubmit)}>
              <SheetHeader>
                <SheetTitle>Create Asset</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-2 overflow-y-auto px-6 pt-4 pb-6">
                <Alert>
                  <AlertDescription>
                    <b>Note:</b> You can bulk import your assets in in the top right of the asset register, or{' '}
                    <span className="cursor-pointer underline" onClick={() => onOpenBulkImport?.(true)}>
                      click here
                    </span>
                    .
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <AssetInputField label="Asset ID" name="internalId" control={form.control} placeholder="Type text..." required />
                  </div>
                  <AssetSelectField
                    label="Asset Type"
                    name="type"
                    control={form.control}
                    options={Object.values(GearbeltAssetType).map((label) => ({ label, value: label })) || []}
                    required
                  />
                  <AssetCreatableSelectField
                    label="Asset Category"
                    name="categoryId"
                    control={form.control}
                    options={assetCategories?.map(({ id, name }) => ({ label: name, value: id })).sort((a, b) => a.label.localeCompare(b.label)) || []}
                  />
                  <AssetCreatableSelectField
                    label="Manufacturer"
                    name="manufacturer"
                    control={form.control}
                    options={manufacturers?.map(({ make }) => ({ label: make, value: make })) || []}
                  />
                  <AssetInputField label="Model" name="model" control={form.control} placeholder="Type text..." />
                  <AssetInputField label="Class" name="class" control={form.control} placeholder="Type text..." />
                  <AssetSelectField
                    label="Year"
                    name="year"
                    control={form.control}
                    options={Array.from({ length: new Date().getFullYear() - 1899 }, (_, i) => 1900 + i)
                      .reverse()
                      .map((year) => ({
                        label: year.toString(),
                        value: year.toString(),
                      }))}
                  />
                  <AssetInputField label="Registration" name="registration" control={form.control} placeholder="Type text..." />
                  <AssetInputField label="Serial Number" name="serialNumber" control={form.control} placeholder="Type text..." />
                  <AssetInputField label="Engine Number" name="engineNumber" control={form.control} placeholder="Type text..." />
                  <AssetInputField label="VIN" name="vin" control={form.control} placeholder="Type text..." />
                  <div className="col-span-2">
                    <AssetTextareaField label="Comments" name="comments" control={form.control} placeholder="Type text..." />
                  </div>
                  <div className="col-span-2">
                    <AssetCreatableMultiSelectField
                      control={form.control}
                      name="tags"
                      label="Tags"
                      defaultOptions={tags?.map(({ tag }) => ({ label: tag, value: tag })) || []}
                      placeholder="Type new tags or select from list..."
                    />
                  </div>
                </div>
              </div>

              <SheetFooter>
                <Button variant="grey" type="button" onClick={() => handleOpenChange(false)} disabled={form.formState.isSubmitting}>
                  Cancel
                </Button>
                <Button variant="success" type="submit" disabled={form.formState.isSubmitting}>
                  Save
                </Button>
              </SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </>
  );
}
